/* eslint-disable react/no-danger */
/* eslint-disable react/no-array-index-key */
/**
 * @fileoverview component Organism to render messages of type carousel in the inbox of Mercately.
 * Allows to show multiple cards with images, videos, buttons and text, optimized for multi-channel platforms.
 * Uses Atomic Design (Organism) and is optimized with React.memo and useMemo for performance.
 *
 * @component CarouselMessageMC
 * @module presentation/modules/inbox/components/CarouselMessageMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.1.0 - Optimization of rendering and atomic structure
 */
import React, { useMemo } from 'react';
import { checkForUrls } from '../../../../util/urlUtil';
import GroupSenderMC from './GroupSenderMC';
import MessageStatusMC from './MessageStatusMC';
import VideoMC from '../../../components/VideoMC';
import ImageMC from '../../../components/ImageMC';

const CarouselMessageMC = ({ message, conversation, onImageClick }) => {
  /**
   * Memoization of the cards of the carousel to optimize renders.
   */
  const cards = useMemo(() => message.carouselCards || [], [message.carouselCards]);

  /**
   * Type of header of the card (video or image).
   */
  const headerType = message.cardHeaderType || 'IMAGE';
  const messageBaseId = message.id || message.identifier || message.message_identifier;

  return (
    <div className="tw-wrapper tw-w-full tw-max-w-full">
      <GroupSenderMC message={message} />

      {message.messageText && (
        <div
          className="message-caption tw-mb-3 tw-whitespace-pre-line tw-text-sm"
          dangerouslySetInnerHTML={{ __html: checkForUrls(message.messageText) }}
        />
      )}

      <div
        className="tw-flex tw-flex-row tw-overflow-x-auto tw-w-full tw-pb-3 tw-gap-3 tw-snap-x tw-snap-mandatory"
        style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}
      >
        {cards.map((card, index) => (
          <div
            key={`${message.id || message.identifier}-card-${index}`}
            className="carousel-card tw-flex-shrink-0 tw-bg-white tw-border tw-rounded tw-shadow-sm tw-overflow-hidden tw-w-[220px] tw-snap-start"
          >
            <div
              className="tw-relative tw-overflow-hidden tw-bg-gray-100 tw-h-[160px]"
            >
              {headerType === 'video' ? (
                <VideoMC
                  platform={conversation.platform}
                  data={card}
                  className="tw-block !tw-w-[320px] !tw-max-h-[200px] tw-rounded-[9px] tw-overflow-hidden tw-border-[#E4E4E4]"
                  urlField="link"
                />
              ) : (
                <ImageMC
                  platform={conversation.platform}
                  data={card}
                  onClick={() => onImageClick?.(`${messageBaseId}-${index}`)}
                  alt={card.body || `Carousel image ${index + 1}`}
                  className="tw-block tw-w-full tw-h-full tw-object-cover tw-rounded-[9px] tw-bg-white tw-cursor-pointer !tw-max-w-none !tw-max-h-none"
                  urlField="link"
                />
              )}
            </div>

            <div className="tw-p-3">
              {card.body && (
                <div
                  className="tw-text-[13px] tw-text-gray-700 tw-font-medium tw-leading-tight tw-mb-2 tw-line-clamp-3"
                >
                  {card.body}
                </div>
              )}

              {card.buttons && card.buttons.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-2">
                  {card.buttons.map((button, btnIdx) => (
                    <button
                      type="button"
                      key={btnIdx}
                      className="tw-w-full tw-px-3 tw-py-2 tw-bg-gray-50 tw-text-blue-600 tw-text-[12px] tw-font-semibold tw-rounded-md tw-border tw-border-blue-100 tw-cursor-not-allowed"
                      disabled
                    >
                      {button.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <MessageStatusMC message={message} platform={conversation.platform} />
    </div>
  );
};

export default CarouselMessageMC;
